/** Shared with Sentry `ignoreErrors` on the client — keep in sync with `shouldCaptureException`. */
export const SENTRY_IGNORED_MESSAGE_PATTERNS = [
	/removeChild/i,
	/not a child of this node/i,
	/The object can not be found here/i,
	/requestFullscreen is not a function/i,
	/requestFullscreen\(\)/i,
	/Cannot read properties of null \(reading 'document'\)/i,
	/Cannot read properties of null \(reading 'status'\)/i,
	/Single fetch-enabled apps should not be making \?_data requests/i,
	/Load failed/i,
	/Failed to fetch/i,
	/NetworkError/i,
	/AbortError/i,
];

/** Remix `ErrorResponseImpl` / `Response` throws (not always `instanceof Error`). */
export function isRemixRouteErrorResponse(
	error: unknown,
): error is { status: number; statusText: string; data: unknown; internal?: boolean } {
	return (
		typeof error === "object" &&
		error !== null &&
		"status" in error &&
		"statusText" in error &&
		typeof (error as { status: unknown }).status === "number"
	);
}

function messageFromUnknown(error: unknown): string {
	if (error instanceof Error) return error.message;
	if (typeof error === "string") return error;
	if (isRemixRouteErrorResponse(error)) return `${error.status} ${error.statusText}`;
	if (error && typeof error === "object" && "message" in error) {
		return String((error as { message: unknown }).message);
	}
	return "";
}

/** Remix route responses (404, etc.) and known benign browser noise. */
export function shouldCaptureException(error: unknown): boolean {
	if (isRemixRouteErrorResponse(error)) {
		return error.status >= 500;
	}

	const message = messageFromUnknown(error);
	if (message && SENTRY_IGNORED_MESSAGE_PATTERNS.some(pattern => pattern.test(message))) {
		return false;
	}

	return true;
}

export function sentryBeforeSend<T extends { message?: string }>(
	event: T,
	hint: { originalException?: unknown },
): T | null {
	const original = hint.originalException;
	if (original !== undefined && !shouldCaptureException(original)) {
		return null;
	}
	if (event.message && SENTRY_IGNORED_MESSAGE_PATTERNS.some(pattern => pattern.test(event.message ?? ""))) {
		return null;
	}
	return event;
}
